import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Linkedin, Instagram, Twitter, Link2, Plus, X as XIcon, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { parseProjectUrls, serializeProjectUrls } from '../../lib/projectUrls';
import { useCategories } from '../../hooks/useCategories';
import { useAuth } from '../../hooks/useAuth';
import BasesModal from '../landing/BasesModal';
import './NominationForm.css';

type SocialType = 'linkedin' | 'instagram' | 'x' | 'otro';
type SocialLink = { type: SocialType; url: string };

const SOCIAL_OPTIONS: { type: SocialType; label: string; Icon: React.ComponentType<{ size?: number }>; placeholder: string }[] = [
  { type: 'linkedin', label: 'LinkedIn', Icon: Linkedin, placeholder: 'https://linkedin.com/in/...' },
  { type: 'instagram', label: 'Instagram', Icon: Instagram, placeholder: 'https://instagram.com/...' },
  { type: 'x', label: 'X (Twitter)', Icon: Twitter, placeholder: 'https://x.com/...' },
  { type: 'otro', label: 'Otro', Icon: Link2, placeholder: 'https://...' },
];

const parseLinks = (value: string): SocialLink[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter((l: any) => l && typeof l.url === 'string');
  } catch {}
  return value
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((url) => ({ type: 'otro' as SocialType, url }));
};

const SocialLinksInput: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const links = useMemo(() => parseLinks(value), [value]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const commit = (next: SocialLink[]) => onChange(next.length ? JSON.stringify(next) : '');
  const addLink = (type: SocialType) => { commit([...links, { type, url: '' }]); setOpen(false); };
  const removeLink = (idx: number) => commit(links.filter((_, i) => i !== idx));
  const setUrl = (idx: number, url: string) => commit(links.map((l, i) => (i === idx ? { ...l, url } : l)));

  return (
    <div className="social-links-input">
      {links.map((link, idx) => {
        const opt = SOCIAL_OPTIONS.find((o) => o.type === link.type) || SOCIAL_OPTIONS[3];
        const Icon = opt.Icon;
        return (
          <div key={idx} className="social-link-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span title={opt.label} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 8, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.85)', flexShrink: 0 }}>
              <Icon size={18} />
            </span>
            <input
              type="url"
              className="form-input"
              style={{ flex: 1 }}
              placeholder={opt.placeholder}
              value={link.url}
              onChange={(e) => setUrl(idx, e.target.value)}
            />
            <button
              type="button"
              onClick={() => removeLink(idx)}
              aria-label="Eliminar"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}
            >
              <XIcon size={16} />
            </button>
          </div>
        );
      })}
      <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="btn btn-secondary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '0.9rem' }}
        >
          <Plus size={16} /> Añadir red social
        </button>
        {open && (
          <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: '#003554', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: 6, zIndex: 50, minWidth: 180, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
            {SOCIAL_OPTIONS.map((opt) => {
              const Icon = opt.Icon;
              return (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => addLink(opt.type)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.9)', cursor: 'pointer', borderRadius: 6, fontSize: '0.9rem', textAlign: 'left' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <Icon size={16} /> {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const NominationForm: React.FC = () => {
  const navigate = useNavigate();
  const { id: nominationId } = useParams<{ id: string }>();
  const isEditing = !!nominationId;
  const { user } = useAuth();

  const { categories, loading: loadingCategories, error: categoriesError } = useCategories();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingNomination, setLoadingNomination] = useState(isEditing);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingDisabledByConfig, setEditingDisabledByConfig] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<typeof formData | null>(null);
  const [submittedCategoryIds, setSubmittedCategoryIds] = useState<Set<string>>(new Set());
  const [acceptedBases, setAcceptedBases] = useState(false);
  const [basesOpen, setBasesOpen] = useState(false);

  // Ámbito del premio: España u Iberoamérica/LATAM. Determina qué
  // categorías se ofrecen en el desplegable. Mucha gente se confundía
  // presentándose a categorías que no le correspondían — esta pregunta
  // previa lo evita.
  const [awardScope, setAwardScope] = useState<'' | 'spain' | 'latam'>('');

  const [formData, setFormData] = useState({
    submitter_name: '',
    submitter_email: '',
    candidate_name: '',
    candidate_reason: '',
    candidate_cv_url: '',
    candidate_social_links: '',
    project_title: '',
    institution: '',
    project_summary: '',
    target_audience: '',
    innovation_description: '',
    collaboration_description: '',
    replicability_description: '',
    impact_description: '',
    cost_benefit_description: '',
    sustainability_description: '',
    project_url: '',
    project_social_links: '',
    // Campos específicos Benchmarking
    benchmark_edicion_categoria: '',
    benchmark_descripcion_breve: '',
    benchmark_impacto_sostenido: '',
    benchmark_elementos_replicables: '',
    benchmark_administraciones_replicadoras: '',
    benchmark_mejoras_aprendizajes: '',
    benchmark_evidencias_enlaces: '',
  });

  // URLs del proyecto como array editable; se persisten como JSON en
  // formData.project_url (misma columna de texto de siempre).
  const [projectUrls, setProjectUrls] = useState<string[]>(['']);

  const updateProjectUrls = (next: string[]) => {
    setProjectUrls(next);
    setFormData(prev => ({ ...prev, project_url: serializeProjectUrls(next) }));
  };

  // Load existing nomination for editing
  useEffect(() => {
    if (isEditing && nominationId && user) {
      checkEditingAndLoad(nominationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nominationId, user]);

  // Block submission when closed (only for new nominations)
  useEffect(() => {
    if (isEditing) return;
    supabase
      .schema('premios')
      .from('platform_config')
      .select('nominations_submission_enabled')
      .single()
      .then(({ data }) => {
        if (data && data.nominations_submission_enabled === false) {
          setError(
            'La presentación de candidaturas no está disponible por el momento. En breve se comunicará la apertura de la próxima convocatoria.'
          );
        }
      });
  }, [isEditing]);

  // Load categories already submitted by this user (to disable them)
  useEffect(() => {
    if (user?.email) {
      loadSubmittedCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Auto-fill submitter name and email from user account on new nomination
  useEffect(() => {
    if (!isEditing && user) {
      const meta: any = user.user_metadata || {};
      const fullName =
        meta.full_name ||
        [meta.name, meta.lastName].filter(Boolean).join(' ').trim() ||
        '';
      setFormData((prev) => ({
        ...prev,
        submitter_email: user.email || '',
        submitter_name: fullName,
      }));
    }
  }, [user, isEditing]);

  const loadSubmittedCategories = async () => {
    const { data } = await supabase
      .schema('premios')
      .from('nominations')
      .select('category_id, id')
      .eq('submitter_email', user?.email);

    if (data) {
      const ids = new Set(
        data
          .filter((n: any) => n.id !== nominationId) // excluye la que se está editando
          .map((n: any) => n.category_id)
      );
      setSubmittedCategoryIds(ids);
    }
  };

  const checkEditingAndLoad = async (id: string) => {
    try {
      setLoadingNomination(true);

      // Check if editing is enabled in platform config
      const { data: configData } = await supabase
        .schema('premios')
        .from('platform_config')
        .select('nominations_editing_enabled')
        .single();

      if (!configData?.nominations_editing_enabled) {
        setEditingDisabledByConfig(true);
        return;
      }

      const { data, error } = await supabase
        .schema('premios')
        .from('nominations')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Candidatura no encontrada');

      if (data.status !== 'pending') {
        setError('Solo puedes editar candidaturas pendientes de revision.');
        return;
      }
      if (data.submitter_email !== user?.email) {
        setError('No tienes permiso para editar esta candidatura.');
        return;
      }

      setSelectedCategoryId(data.category_id);
      // Detecta el ámbito a partir del nombre de la categoría cargada
      const loadedCat = (categories || []).find((c) => c.id === data.category_id);
      if (loadedCat) {
        setAwardScope(/LATAM/i.test(loadedCat.name) ? 'latam' : 'spain');
      }
      const loaded = {
        submitter_name: data.submitter_name || '',
        submitter_email: data.submitter_email || '',
        candidate_name: data.candidate_name || '',
        candidate_reason: data.candidate_reason || '',
        candidate_cv_url: data.candidate_cv_url || '',
        candidate_social_links: data.candidate_social_links || '',
        project_title: data.project_title || '',
        institution: data.institution || '',
        project_summary: data.project_summary || '',
        target_audience: data.target_audience || '',
        innovation_description: data.innovation_description || '',
        collaboration_description: data.collaboration_description || '',
        replicability_description: data.replicability_description || '',
        impact_description: data.impact_description || '',
        cost_benefit_description: data.cost_benefit_description || '',
        sustainability_description: data.sustainability_description || '',
        project_url: serializeProjectUrls(parseProjectUrls(data.project_url)),
        project_social_links: data.project_social_links || '',
        benchmark_edicion_categoria: data.benchmark_edicion_categoria || '',
        benchmark_descripcion_breve: data.benchmark_descripcion_breve || '',
        benchmark_impacto_sostenido: data.benchmark_impacto_sostenido || '',
        benchmark_elementos_replicables: data.benchmark_elementos_replicables || '',
        benchmark_administraciones_replicadoras: data.benchmark_administraciones_replicadoras || '',
        benchmark_mejoras_aprendizajes: data.benchmark_mejoras_aprendizajes || '',
        benchmark_evidencias_enlaces: data.benchmark_evidencias_enlaces || '',
      };
      setFormData(loaded);
      setOriginalFormData(loaded);
      const loadedUrls = parseProjectUrls(data.project_url);
      setProjectUrls(loadedUrls.length ? loadedUrls : ['']);
    } catch (err: any) {
      console.error('Error loading nomination:', err);
      setError(err.message || 'Error al cargar la candidatura');
    } finally {
      setLoadingNomination(false);
    }
  };

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);
  const isPersonal = selectedCategory?.type === 'personal';
  const isBenchmark = selectedCategory?.type === 'benchmark';

  const hasChanges = isEditing && originalFormData
    ? JSON.stringify(formData) !== JSON.stringify(originalFormData)
    : true;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategoryId) {
      setError('Por favor, selecciona una categoria');
      return;
    }
    if (!isEditing && !acceptedBases) {
      setError('Debes confirmar que has leído y aceptas las bases de la convocatoria.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const nominationData: any = {
        category_id: selectedCategoryId,
        submitter_name: formData.submitter_name,
        submitter_email: user?.email || formData.submitter_email,
      };

      if (!isEditing) {
        nominationData.status = 'pending';
      }

      if (isPersonal) {
        // For personal categories, the candidate is the submitter
        nominationData.candidate_name = formData.submitter_name;
        nominationData.candidate_reason = formData.candidate_reason;
        nominationData.candidate_cv_url = formData.candidate_cv_url;
        nominationData.candidate_social_links = formData.candidate_social_links;
      } else if (isBenchmark) {
        // Benchmarking usa un set propio de campos
        nominationData.project_title = formData.submitter_name; // ref al proyecto
        nominationData.benchmark_edicion_categoria = formData.benchmark_edicion_categoria;
        nominationData.benchmark_descripcion_breve = formData.benchmark_descripcion_breve;
        nominationData.benchmark_impacto_sostenido = formData.benchmark_impacto_sostenido;
        nominationData.benchmark_elementos_replicables = formData.benchmark_elementos_replicables;
        nominationData.benchmark_administraciones_replicadoras = formData.benchmark_administraciones_replicadoras;
        nominationData.benchmark_mejoras_aprendizajes = formData.benchmark_mejoras_aprendizajes;
        nominationData.benchmark_evidencias_enlaces = formData.benchmark_evidencias_enlaces;
      } else {
        nominationData.project_title = formData.project_title;
        nominationData.institution = formData.institution;
        nominationData.project_summary = formData.project_summary;
        nominationData.target_audience = formData.target_audience;
        nominationData.innovation_description = formData.innovation_description;
        nominationData.collaboration_description = formData.collaboration_description;
        nominationData.replicability_description = formData.replicability_description;
        nominationData.impact_description = formData.impact_description;
        nominationData.cost_benefit_description = formData.cost_benefit_description;
        nominationData.sustainability_description = formData.sustainability_description;
        nominationData.project_url = serializeProjectUrls(projectUrls);
        nominationData.project_social_links = formData.project_social_links;
      }

      if (isEditing) {
        nominationData.updated_at = new Date().toISOString();
        const { error: updateError } = await supabase
          .schema('premios')
          .from('nominations')
          .update(nominationData)
          .eq('id', nominationId)
          .eq('status', 'pending');

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .schema('premios')
          .from('nominations')
          .insert([nominationData]);

        if (insertError) throw insertError;
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Error submitting nomination:', err);
      setError(err.message || 'Error al enviar la candidatura');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="container" style={{ marginTop: '60px', marginBottom: '60px' }}>
        <div
          className="card"
          style={{
            maxWidth: 560,
            margin: '0 auto',
            textAlign: 'center',
            padding: '48px 32px',
          }}
        >
          <div
            style={{
              width: 84,
              height: 84,
              borderRadius: '50%',
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(16, 185, 129, 0.15)',
              border: '2px solid rgba(16, 185, 129, 0.5)',
            }}
          >
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h2 style={{ marginBottom: 12, color: '#fff' }}>
            {isEditing ? '¡Candidatura actualizada!' : '¡Candidatura enviada!'}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: 6 }}>
            Gracias por participar en los Premios NovaGob Excelencia {new Date().getFullYear()}.
          </p>
          {!isEditing && (
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', marginBottom: 0 }}>
              Tu candidatura será revisada por el equipo de NovaGob.
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loadingCategories || loadingNomination) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (editingDisabledByConfig) {
    return (
      <div className="container" style={{ marginTop: '40px' }}>
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h2 style={{ color: 'var(--color-secondary)' }}>Edicion no disponible</h2>
          <p style={{ marginTop: '12px', color: 'var(--color-text-secondary)' }}>
            La edicion de candidaturas esta desactivada en este momento. Contacta con el equipo de NovaGob si necesitas modificar tu candidatura.
          </p>
          <button className="btn btn-secondary" style={{ marginTop: '20px' }} onClick={() => navigate('/dashboard')}>
            Volver a Mis Candidaturas
          </button>
        </div>
      </div>
    );
  }

  if (
    error &&
    (isEditing ||
      error.includes('plazo') ||
      error.includes('convocatoria'))
  ) {
    return (
      <div className="container" style={{ marginTop: '40px' }}>
        <div className="card">
          <div className="alert alert-error">{error}</div>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ marginTop: '40px', marginBottom: '80px' }}>
      <div className="card">
        <div className="form-header">
          <h1>{isEditing ? 'Editar Candidatura' : 'Presentar Candidatura'}</h1>
          <p>Premios NovaGob Excelencia {new Date().getFullYear()}</p>
        </div>

        {categoriesError && (
          <div className="alert alert-error">
            Error al cargar las categorias: {categoriesError.message}
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="nomination-form">
          <div className="form-group">
            <label className="form-label">Nombre *</label>
            <input
              type="text"
              name="submitter_name"
              className="form-input"
              value={formData.submitter_name}
              onChange={handleChange}
              required
            />
          </div>

          {!isEditing && (
            <div className="form-group">
              <label className="form-label">
                ¿Desde dónde se presenta la candidatura? *
              </label>
              <div
                role="radiogroup"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                  marginTop: 6,
                }}
              >
                {[
                  {
                    value: 'spain',
                    title: 'España',
                    subtitle: 'Persona o proyecto que se desarrolla en España',
                  },
                  {
                    value: 'latam',
                    title: 'Latinoamérica',
                    subtitle: 'Persona o proyecto que se desarrolla en LATAM',
                  },
                ].map((opt) => {
                  const active = awardScope === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        if (awardScope !== opt.value) {
                          setAwardScope(opt.value as 'spain' | 'latam');
                          setSelectedCategoryId('');
                        }
                      }}
                      style={{
                        padding: '14px 16px',
                        borderRadius: 12,
                        background: active
                          ? 'rgba(255, 215, 2, 0.12)'
                          : 'rgba(255, 255, 255, 0.04)',
                        border: active
                          ? '1.5px solid rgba(255, 215, 2, 0.6)'
                          : '1px solid rgba(255, 255, 255, 0.12)',
                        color: '#fff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 15, color: active ? '#ffd702' : '#fff' }}>
                        {opt.title}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                        {opt.subtitle}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="form-helper" style={{ marginTop: 8 }}>
                Según el lugar verás las categorías que corresponden — las mismas bases para todos los Premios NovaGob.
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Categoria *</label>
            <select
              className="form-select"
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              required
              disabled={isEditing || (!isEditing && !awardScope)}
            >
              <option value="">
                {!isEditing && !awardScope
                  ? 'Primero indica desde dónde se presenta'
                  : 'Selecciona una categoria'}
              </option>
              {categories
                .filter((cat) => {
                  if (isEditing) return true;
                  if (!awardScope) return false;
                  const isLatam = /LATAM/i.test(cat.name);
                  return awardScope === 'latam' ? isLatam : !isLatam;
                })
                .map((cat) => {
                  const alreadySubmitted = submittedCategoryIds.has(cat.id);
                  const isDisabled = !isEditing && (!cat.is_open || alreadySubmitted);
                  let suffix = '';
                  if (alreadySubmitted && !isEditing) suffix = ' - Ya presentada';
                  else if (!cat.is_open && !isEditing) suffix = ' (CERRADA)';
                  return (
                    <option key={cat.id} value={cat.id} disabled={isDisabled}>
                      {cat.name}{suffix}
                    </option>
                  );
                })}
            </select>
            {isEditing && (
              <div className="form-helper">La categoria no se puede cambiar al editar.</div>
            )}
          </div>

          {selectedCategoryId && (
            <>
              {isBenchmark ? (
                <div className="benchmark-fields anim-fade-in">
                  <div className="form-group">
                    <label className="form-label">
                      Edición/categoría en la que fue premiado *
                    </label>
                    <input
                      type="text"
                      name="benchmark_edicion_categoria"
                      className="form-input"
                      value={formData.benchmark_edicion_categoria}
                      onChange={handleChange}
                      placeholder="Ej. 2023 — Creatividad en la innovación"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Descripción breve del proyecto (máx. 600 caracteres) *
                    </label>
                    <textarea
                      name="benchmark_descripcion_breve"
                      className="form-textarea"
                      rows={3}
                      maxLength={600}
                      value={formData.benchmark_descripcion_breve}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Impacto sostenido acreditado (máx. 1200 caracteres) *
                    </label>
                    <textarea
                      name="benchmark_impacto_sostenido"
                      className="form-textarea"
                      rows={5}
                      maxLength={1200}
                      value={formData.benchmark_impacto_sostenido}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Elementos que lo hacen replicable (máx. 800 caracteres) *
                    </label>
                    <textarea
                      name="benchmark_elementos_replicables"
                      className="form-textarea"
                      rows={4}
                      maxLength={800}
                      value={formData.benchmark_elementos_replicables}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Administraciones que lo han replicado o tomado como referencia (máx. 600 caracteres) *
                    </label>
                    <textarea
                      name="benchmark_administraciones_replicadoras"
                      className="form-textarea"
                      rows={3}
                      maxLength={600}
                      value={formData.benchmark_administraciones_replicadoras}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Mejoras o aprendizajes que ha inspirado (máx. 1200 caracteres) *
                    </label>
                    <textarea
                      name="benchmark_mejoras_aprendizajes"
                      className="form-textarea"
                      rows={5}
                      maxLength={1200}
                      value={formData.benchmark_mejoras_aprendizajes}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Evidencias y enlaces de verificación
                    </label>
                    <textarea
                      name="benchmark_evidencias_enlaces"
                      className="form-textarea"
                      rows={3}
                      value={formData.benchmark_evidencias_enlaces}
                      onChange={handleChange}
                      placeholder="Un enlace por línea o texto con referencias."
                    />
                    <div className="form-helper">
                      Opcional. Documentación, noticias, estudios o enlaces que
                      avalen el impacto y la replicabilidad.
                    </div>
                  </div>
                </div>
              ) : isPersonal ? (
                <div className="personal-fields anim-fade-in">
                  <div className="form-group">
                    <label className="form-label">Justificacion de la candidatura (max. 1500 caracteres) *</label>
                    <textarea
                      name="candidate_reason"
                      className="form-textarea"
                      rows={6}
                      maxLength={1500}
                      value={formData.candidate_reason}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>
                  <div className="form-group">
                    <label className="form-label">URL del CV *</label>
                    <input
                      type="url"
                      name="candidate_cv_url"
                      className="form-input"
                      value={formData.candidate_cv_url}
                      onChange={handleChange}
                      placeholder="https://..."
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Redes Sociales y otros enlaces</label>
                    <SocialLinksInput
                      value={formData.candidate_social_links}
                      onChange={(v) => setFormData((prev) => ({ ...prev, candidate_social_links: v }))}
                    />
                  </div>
                </div>
              ) : (
                <div className="project-fields anim-fade-in">
                  <div className="grid grid-2">
                    <div className="form-group">
                      <label className="form-label">Titulo de la Iniciativa *</label>
                      <input
                        type="text"
                        name="project_title"
                        className="form-input"
                        value={formData.project_title}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Institucion *</label>
                      <input
                        type="text"
                        name="institution"
                        className="form-input"
                        value={formData.institution}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Resumen de la iniciativa (max. 600 caracteres) *</label>
                    <textarea
                      name="project_summary"
                      className="form-textarea"
                      rows={3}
                      maxLength={600}
                      value={formData.project_summary}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Publico Objetivo (max. 600 caracteres) *</label>
                    <textarea
                      name="target_audience"
                      className="form-textarea"
                      rows={2}
                      maxLength={600}
                      value={formData.target_audience}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Innovacion Aportada (max. 1200 caracteres) *</label>
                    <textarea
                      name="innovation_description"
                      className="form-textarea"
                      rows={4}
                      maxLength={1200}
                      value={formData.innovation_description}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Colaboracion (max. 800 caracteres) *</label>
                    <textarea
                      name="collaboration_description"
                      className="form-textarea"
                      rows={3}
                      maxLength={800}
                      value={formData.collaboration_description}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Replicabilidad y Benchmarking (max. 700 caracteres) *</label>
                    <textarea
                      name="replicability_description"
                      className="form-textarea"
                      rows={3}
                      maxLength={700}
                      value={formData.replicability_description}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Impacto y Evaluacion (max. 1200 caracteres) *</label>
                    <textarea
                      name="impact_description"
                      className="form-textarea"
                      rows={4}
                      maxLength={1200}
                      value={formData.impact_description}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="grid grid-2">
                    <div className="form-group">
                      <label className="form-label">Equilibrio Coste/Beneficio (max. 600 caracteres) *</label>
                      <textarea
                        name="cost_benefit_description"
                        className="form-textarea"
                        rows={3}
                        maxLength={600}
                        value={formData.cost_benefit_description}
                        onChange={handleChange}
                        required
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Sostenibilidad (max. 600 caracteres) *</label>
                      <textarea
                        name="sustainability_description"
                        className="form-textarea"
                        rows={3}
                        maxLength={600}
                        value={formData.sustainability_description}
                        onChange={handleChange}
                        required
                      ></textarea>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">URL del Proyecto</label>
                    {projectUrls.map((url, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <input
                          type="url"
                          className="form-input"
                          style={{ flex: 1 }}
                          value={url}
                          onChange={(e) => {
                            const next = [...projectUrls];
                            next[idx] = e.target.value;
                            updateProjectUrls(next);
                          }}
                          placeholder="https://..."
                          required={idx === 0}
                        />
                        {idx > 0 && (
                          <button
                            type="button"
                            onClick={() => updateProjectUrls(projectUrls.filter((_, i) => i !== idx))}
                            aria-label="Eliminar"
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', flexShrink: 0 }}
                          >
                            <XIcon size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: '0.9rem', marginTop: 4 }}
                      onClick={() => updateProjectUrls([...projectUrls, ''])}
                      disabled={!projectUrls[projectUrls.length - 1]?.trim()}
                    >
                      <Plus size={16} /> Añadir otra URL
                    </button>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Redes Sociales del Proyecto</label>
                    <SocialLinksInput
                      value={formData.project_social_links}
                      onChange={(v) => setFormData((prev) => ({ ...prev, project_social_links: v }))}
                    />
                  </div>
                </div>
              )}

              {!isEditing && (
                <div className="form-group bases-acceptance">
                  <label className="bases-acceptance-label">
                    <input
                      type="checkbox"
                      checked={acceptedBases}
                      onChange={(e) => setAcceptedBases(e.target.checked)}
                      required
                    />
                    <span>
                      He leído y acepto las{' '}
                      <button
                        type="button"
                        className="bases-acceptance-link"
                        onClick={() => setBasesOpen(true)}
                      >
                        <FileText size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                        bases de la convocatoria
                      </button>
                      , y declaro que la candidatura que presento se ajusta a
                      ellas. *
                    </span>
                  </label>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                {isEditing && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ flex: 1 }}
                    onClick={() => navigate('/dashboard')}
                  >
                    Volver
                  </button>
                )}
                {isEditing ? (
                  hasChanges && (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{ flex: 1 }}
                      disabled={loading}
                    >
                      {loading ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                  )
                ) : (
                  <button
                    type="submit"
                    className="btn btn-primary w-full"
                    disabled={loading || !acceptedBases}
                    title={!acceptedBases ? 'Debes aceptar las bases antes de enviar' : undefined}
                  >
                    {loading ? 'Enviando...' : 'Enviar Candidatura'}
                  </button>
                )}
              </div>
            </>
          )}
        </form>
      </div>

      <BasesModal open={basesOpen} onClose={() => setBasesOpen(false)} />
    </div>
  );
};

export default NominationForm;
